import React, { useState, useEffect } from 'react';
import {
  Box,
  VStack,

  Text,
  Heading,
  Card,
  CardBody,
  CardHeader,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  Button,
  Spinner,
  Alert,
  AlertIcon,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  SimpleGrid,
  Divider,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  useDisclosure,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Icon,
  Container
} from '@chakra-ui/react';
import { FaEye, FaClock, FaUsers, FaChartLine } from 'react-icons/fa';
import { supabase } from '../../supabaseClient';

const PitchDeckAnalytics = () => {
  const [accessLogs, setAccessLogs] = useState([]);
  const [analytics, setAnalytics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userDetails, setUserDetails] = useState([]);
  const [stats, setStats] = useState({
    totalAccesses: 0,
    uniqueUsers: 0,
    avgTimeSpent: 0,
    totalSessions: 0
  });
  
  const { isOpen, onOpen, onClose } = useDisclosure();

  useEffect(() => {
    fetchAnalyticsData();
  }, []);

  const fetchAnalyticsData = async () => {
    setLoading(true);
    try {
      // Fetch access logs
      const { data: accessData, error: accessError } = await supabase
        .from('pitch_deck_access_log')
        .select('*')
        .order('accessed_at', { ascending: false });

      if (accessError) throw accessError;

      // Fetch analytics data
      const { data: analyticsData, error: analyticsError } = await supabase
        .from('pitch_deck_analytics')
        .select('*')
        .order('timestamp', { ascending: false });

      if (analyticsError) throw analyticsError;

      setAccessLogs(accessData || []);
      setAnalytics(analyticsData || []);

      // Calculate stats
      const uniqueEmails = new Set(accessData?.map(log => log.email) || []);
      const uniqueSessions = new Set(analyticsData?.map(item => item.session_id) || []);
      const avgTime = analyticsData?.length > 0 
        ? analyticsData.reduce((sum, item) => sum + (item.time_spent || 0), 0) / analyticsData.length
        : 0;

      setStats({
        totalAccesses: accessData?.length || 0,
        uniqueUsers: uniqueEmails.size,
        avgTimeSpent: Math.round(avgTime),
        totalSessions: uniqueSessions.size
      });

    } catch (error) {
      console.error('Error fetching analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleUserClick = async (email) => {
    setSelectedUser(email);
    
    try {
      // Fetch all access logs for this user
      const { data: userAccess, error: accessError } = await supabase
        .from('pitch_deck_access_log')
        .select('*')
        .eq('email', email)
        .order('accessed_at', { ascending: false });

      // Fetch all analytics for this user
      const { data: userAnalytics, error: analyticsError } = await supabase
        .from('pitch_deck_analytics')
        .select('*')
        .eq('email', email)
        .order('timestamp', { ascending: false });

      if (accessError || analyticsError) {
        throw accessError || analyticsError;
      }

      setUserDetails({
        accessLogs: userAccess || [],
        analytics: userAnalytics || []
      });
      onOpen();
    } catch (error) {
      console.error('Error fetching user details:', error);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString();
  };

  const formatDuration = (seconds) => {
    if (seconds < 60) return `${seconds}s`;
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m ${seconds % 60}s`;
    return `${Math.floor(seconds / 3600)}h ${Math.floor((seconds % 3600) / 60)}m`;
  };

  const getCurrentlyActive = () => {
    const now = new Date();
    const fiveMinutesAgo = new Date(now.getTime() - 5 * 60 * 1000);
    
    return analytics.filter(item => 
      new Date(item.timestamp) > fiveMinutesAgo
    );
  };

  if (loading) {
    return (
      <Box p={8} textAlign="center">
        <Spinner size="xl" />
        <Text mt={4}>Loading analytics...</Text>
      </Box>
    );
  }

  const activeUsers = getCurrentlyActive();

  return (
    <Container maxW="7xl" p={6}>
      <VStack spacing={6} align="stretch">
        <Heading size="lg" color="blue.600">
          Pitch Deck Analytics Dashboard
        </Heading>

        {/* Stats Overview */}
        <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={6}>
          <Card>
            <CardBody>
              <Stat>
                <StatLabel>Total Accesses</StatLabel>
                <StatNumber color="blue.600">{stats.totalAccesses}</StatNumber>
                <StatHelpText>
                  <Icon as={FaEye} mr={1} />
                  All time
                </StatHelpText>
              </Stat>
            </CardBody>
          </Card>

          <Card>
            <CardBody>
              <Stat>
                <StatLabel>Unique Users</StatLabel>
                <StatNumber color="green.600">{stats.uniqueUsers}</StatNumber>
                <StatHelpText>
                  <Icon as={FaUsers} mr={1} />
                  Individual viewers
                </StatHelpText>
              </Stat>
            </CardBody>
          </Card>

          <Card>
            <CardBody>
              <Stat>
                <StatLabel>Avg. Time Spent</StatLabel>
                <StatNumber color="purple.600">{formatDuration(stats.avgTimeSpent)}</StatNumber>
                <StatHelpText>
                  <Icon as={FaClock} mr={1} />
                  Per session
                </StatHelpText>
              </Stat>
            </CardBody>
          </Card>

          <Card>
            <CardBody>
              <Stat>
                <StatLabel>Active Now</StatLabel>
                <StatNumber color="orange.600">{activeUsers.length}</StatNumber>
                <StatHelpText>
                  <Icon as={FaChartLine} mr={1} />
                  Last 5 minutes
                </StatHelpText>
              </Stat>
            </CardBody>
          </Card>
        </SimpleGrid>

        {/* Active Users Alert */}
        {activeUsers.length > 0 && (
          <Alert status="info" borderRadius="md">
            <AlertIcon />
            <Box>
              <Text fontWeight="bold">Currently Active Users:</Text>
              <Text>
                {Array.from(new Set(activeUsers.map(u => u.email))).join(', ')} 
                {activeUsers.length === 1 ? ' is' : ' are'} currently viewing the presentation
              </Text>
            </Box>
          </Alert>
        )}

        <Tabs>
          <TabList>
            <Tab>Access Logs</Tab>
            <Tab>Live Activity</Tab>
          </TabList>

          <TabPanels>
            {/* Access Logs Tab */}
            <TabPanel px={0}>
              <Card>
                <CardHeader>
                  <Heading size="md">Access History</Heading>
                  <Text color="gray.600" fontSize="sm">
                    Click on any email to view detailed user activity
                  </Text>
                </CardHeader>
                <CardBody>
                  <Box overflowX="auto">
                    <Table variant="simple">
                      <Thead>
                        <Tr>
                          <Th>Email</Th>
                          <Th>Access Date</Th>
                          <Th>User Agent</Th>
                          <Th>Status</Th>
                        </Tr>
                      </Thead>
                      <Tbody>
                        {accessLogs.map((log) => (
                          <Tr key={log.id} _hover={{ bg: 'gray.50' }}>
                            <Td>
                              <Button
                                variant="link"
                                color="blue.600"
                                fontWeight="medium"
                                onClick={() => handleUserClick(log.email)}
                              >
                                {log.email}
                              </Button>
                            </Td>
                            <Td>{formatDate(log.accessed_at)}</Td>
                            <Td>
                              <Text fontSize="sm" color="gray.600" maxW="200px" isTruncated>
                                {log.user_agent}
                              </Text>
                            </Td>
                            <Td>
                              <Badge colorScheme="green">Accessed</Badge>
                            </Td>
                          </Tr>
                        ))}
                      </Tbody>
                    </Table>
                  </Box>
                </CardBody>
              </Card>
            </TabPanel>

            {/* Live Activity Tab */}
            <TabPanel px={0}>
              <Card>
                <CardHeader>
                  <Heading size="md">Real-time Activity</Heading>
                  <Text color="gray.600" fontSize="sm">
                    Page views and user interactions within the presentation
                  </Text>
                </CardHeader>
                <CardBody>
                  <Box overflowX="auto">
                    <Table variant="simple">
                      <Thead>
                        <Tr>
                          <Th>Email</Th>
                          <Th>Page/Slide</Th>
                          <Th>Timestamp</Th>
                          <Th>Time Spent</Th>
                          <Th>Session</Th>
                        </Tr>
                      </Thead>
                      <Tbody>
                        {analytics.slice(0, 50).map((item) => (
                          <Tr key={item.id}>
                            <Td>
                              <Button
                                variant="link"
                                color="blue.600"
                                fontWeight="medium"
                                onClick={() => handleUserClick(item.email)}
                              >
                                {item.email}
                              </Button>
                            </Td>
                            <Td>
                              <Badge colorScheme="blue" variant="subtle">
                                {item.page}
                              </Badge>
                            </Td>
                            <Td>{formatDate(item.timestamp)}</Td>
                            <Td>{formatDuration(item.time_spent || 0)}</Td>
                            <Td>
                              <Text fontSize="xs" color="gray.500" fontFamily="mono">
                                {item.session_id.slice(-8)}
                              </Text>
                            </Td>
                          </Tr>
                        ))}
                      </Tbody>
                    </Table>
                  </Box>
                </CardBody>
              </Card>
            </TabPanel>
          </TabPanels>
        </Tabs>
      </VStack>

      {/* User Detail Modal */}
      <Modal isOpen={isOpen} onClose={onClose} size="6xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            User Activity: {selectedUser}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            {userDetails.accessLogs && (
              <VStack spacing={6} align="stretch">
                <Box>
                  <Heading size="sm" mb={3}>Access History</Heading>
                  <Table variant="simple" size="sm">
                    <Thead>
                      <Tr>
                        <Th>Date & Time</Th>
                        <Th>IP Address</Th>
                        <Th>User Agent</Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {userDetails.accessLogs.map((log) => (
                        <Tr key={log.id}>
                          <Td>{formatDate(log.accessed_at)}</Td>
                          <Td>{log.ip_address || 'Unknown'}</Td>
                          <Td>
                            <Text fontSize="xs" maxW="300px" isTruncated>
                              {log.user_agent}
                            </Text>
                          </Td>
                        </Tr>
                      ))}
                    </Tbody>
                  </Table>
                </Box>

                <Divider />

                <Box>
                  <Heading size="sm" mb={3}>Page Activity</Heading>
                  <Table variant="simple" size="sm">
                    <Thead>
                      <Tr>
                        <Th>Page/Slide</Th>
                        <Th>Timestamp</Th>
                        <Th>Time Spent</Th>
                        <Th>Session</Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {userDetails.analytics?.map((item) => (
                        <Tr key={item.id}>
                          <Td>
                            <Badge colorScheme="blue" variant="subtle">
                              {item.page}
                            </Badge>
                          </Td>
                          <Td>{formatDate(item.timestamp)}</Td>
                          <Td>{formatDuration(item.time_spent || 0)}</Td>
                          <Td>
                            <Text fontSize="xs" color="gray.500" fontFamily="mono">
                              {item.session_id.slice(-8)}
                            </Text>
                          </Td>
                        </Tr>
                      ))}
                    </Tbody>
                  </Table>
                </Box>
              </VStack>
            )}
          </ModalBody>
        </ModalContent>
      </Modal>
    </Container>
  );
};

export default PitchDeckAnalytics; 